import { getContrastColor } from '@/lib/colorUtils';
import { useState, useEffect } from "react";
import { Sparkles, BarChart3, Building2, Home, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow, format } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { logger } from '@/lib/logger';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/RichTextEditor";

interface NorthAISummariesProps {
  northId: string;
  northData: any;
  executiveSummary?: string | null;
  executiveSummaryUpdatedAt?: string | null;
  showingsSummary?: string | null;
  showingsSummaryUpdatedAt?: string | null;
  compsSummary?: string | null;
  compsSummaryUpdatedAt?: string | null;
  neighborhoodSummary?: string | null;
  neighborhoodSummaryUpdatedAt?: string | null;
  neighborhoodDataUpdatedAt?: string | null;
  isPublished?: boolean;
  secondaryColor?: string | null;
  onUpdate: () => void;
}

export const NorthAISummaries = ({
  northId,
  northData,
  executiveSummary,
  executiveSummaryUpdatedAt,
  showingsSummary,
  showingsSummaryUpdatedAt,
  compsSummary,
  compsSummaryUpdatedAt,
  neighborhoodSummary,
  neighborhoodSummaryUpdatedAt,
  neighborhoodDataUpdatedAt,
  isPublished = false,
  secondaryColor,
  onUpdate,
}: NorthAISummariesProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [execSummary, setExecSummary] = useState(executiveSummary || "");
  const [showSummary, setShowSummary] = useState(showingsSummary || "");
  const [compSummary, setCompSummary] = useState(compsSummary || "");
  const [neighborhoodSum, setNeighborhoodSum] = useState(neighborhoodSummary || "");
  const [generating, setGenerating] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [changesDialogOpen, setChangesDialogOpen] = useState(false);
  const [pendingSummaryType, setPendingSummaryType] = useState<"executive" | "showings" | "comps" | "neighborhood" | null>(null);
  const [requestedChanges, setRequestedChanges] = useState("");
  const [agentInsightsSummary, setAgentInsightsSummary] = useState("");

  // Fetch agent insights for display
  const { data: agentInsights } = useQuery({
    queryKey: ['agent-insights-summary', northId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agent_insights')
        .select('*')
        .eq('north_id', northId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  // Update local state when agent insights data changes
  useEffect(() => {
    if (agentInsights?.ai_summary) {
      setAgentInsightsSummary(agentInsights.ai_summary);
    }
  }, [agentInsights]);

  // Sync local state with props when they change
  useEffect(() => {
    setExecSummary(executiveSummary || "");
  }, [executiveSummary]);

  useEffect(() => {
    setShowSummary(showingsSummary || "");
  }, [showingsSummary]);

  useEffect(() => {
    setCompSummary(compsSummary || "");
  }, [compsSummary]);

  useEffect(() => {
    setNeighborhoodSum(neighborhoodSummary || "");
  }, [neighborhoodSummary]);

  const handleGenerateClick = (type: "executive" | "showings" | "comps" | "neighborhood") => {
    // Check if a summary already exists
    const existingSummary =
      type === "executive" ? execSummary :
      type === "showings" ? showSummary :
      type === "neighborhood" ? neighborhoodSum :
      compSummary;

    if (existingSummary && existingSummary.trim()) {
      // Show dialog to ask for changes
      setPendingSummaryType(type);
      setChangesDialogOpen(true);
    } else {
      // Generate new summary directly
      generateSummary(type);
    }
  };

  const handleConfirmChanges = () => {
    if (pendingSummaryType) {
      generateSummary(pendingSummaryType, requestedChanges);
    }
    setChangesDialogOpen(false);
    setRequestedChanges("");
    setPendingSummaryType(null);
  };

  const generateSummary = async (type: "executive" | "showings" | "comps" | "neighborhood", changes?: string) => {
    setGenerating(type);
    try {
      // Get existing summary
      const existingSummary =
        type === "executive" ? execSummary :
        type === "showings" ? showSummary :
        type === "neighborhood" ? neighborhoodSum :
        compSummary;

      const functionName = type === "neighborhood" ? "generate-neighborhood-summary" : "generate-summary";
      const body = type === "neighborhood" 
        ? {
            northData,
            existingSummary: existingSummary || undefined,
            requestedChanges: changes || undefined,
          }
        : {
            northData,
            summaryType: type,
            existingSummary: existingSummary || undefined,
            requestedChanges: changes || undefined,
          };

      const { data, error } = await supabase.functions.invoke(functionName, { body });

      if (error) throw error;

      if (data.error) {
        toast({
          title: "Error",
          description: data.error,
          variant: "destructive",
        });
        return;
      }

      const summary = data.summary;

      // Update local state
      if (type === "executive") {
        setExecSummary(summary);
      } else if (type === "showings") {
        setShowSummary(summary);
      } else if (type === "neighborhood") {
        setNeighborhoodSum(summary);
      } else if (type === "comps") {
        setCompSummary(summary);
      }

      // Save to database
      const updateField =
        type === "executive"
          ? { executive_summary: summary, executive_summary_updated_at: new Date().toISOString() }
          : type === "showings"
          ? { showings_summary: summary, showings_summary_updated_at: new Date().toISOString() }
          : type === "neighborhood"
          ? { neighborhood_summary: summary, neighborhood_summary_updated_at: new Date().toISOString() }
          : { comps_summary: summary, comps_summary_updated_at: new Date().toISOString() };

      const { error: updateError } = await supabase
        .from("norths")
        .update(updateField)
        .eq("id", northId);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Summary generated successfully",
      });

      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));

      // Notify parent component to refetch north data (for preview update)
      onUpdate();
    } catch (error) {
      logger.error("Error generating summary:", error);
      toast({
        title: "Error",
        description: "Failed to generate summary",
        variant: "destructive",
      });
    } finally {
      setGenerating(null);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("norths")
        .update({
          executive_summary: execSummary || null,
          showings_summary: showSummary || null,
          comps_summary: compSummary || null,
          neighborhood_summary: neighborhoodSum || null,
        })
        .eq("id", northId);

      if (error) throw error;

      // Invalidate all related queries to sync across components
      queryClient.invalidateQueries({ queryKey: ['north-showings-summary', northId] });
      queryClient.invalidateQueries({ queryKey: ['comps', northId] });
      queryClient.invalidateQueries({ queryKey: ['ai-summaries', northId] });

      toast({
        title: "Success",
        description: "Summaries saved successfully",
      });

      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));

      // Notify parent component to refetch north data (for preview update)
      onUpdate();
    } catch (error) {
      logger.error("Error saving summaries:", error);
      toast({
        title: "Error",
        description: "Failed to save summaries",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Auto-save individual summaries on blur
  const handleSaveSummary = async (type: "executive" | "showings" | "comps" | "neighborhood", summary: string) => {
    try {
      const updateField =
        type === "executive"
          ? { executive_summary: summary }
          : type === "showings"
          ? { showings_summary: summary }
          : type === "neighborhood"
          ? { neighborhood_summary: summary }
          : { comps_summary: summary };

      const { error } = await supabase
        .from("norths")
        .update(updateField)
        .eq("id", northId);

      if (error) throw error;

      // Invalidate all related queries to sync across components
      queryClient.invalidateQueries({ queryKey: ['north-showings-summary', northId] });
      queryClient.invalidateQueries({ queryKey: ['comps', northId] });
      queryClient.invalidateQueries({ queryKey: ['ai-summaries', northId] });

      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));

      // Notify parent component to refetch north data (for preview update)
      onUpdate();
    } catch (error) {
      logger.error(`Error saving ${type} summary:`, error);
    }
  };

  const formatLastUpdated = (date: string | null | undefined) => {
    if (!date) return "Never";
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return "Never";
    }
  };

  const formatDataDate = (date: string | null | undefined) => {
    if (!date) return "Unknown";
    try {
      return format(new Date(date), "MMMM d, yyyy");
    } catch {
      return "Unknown";
    }
  };

  return (
    <div className="space-y-6 md:space-y-8 p-4 md:p-6">
      {isPublished && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            This report is published. Click "Unpublish" to make changes.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-2">AI Summaries</h2>
          <p className="text-sm md:text-base text-muted-foreground">
            Generate AI-powered summaries that turn report details into a clear narrative for your seller. You can edit them anytime.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Button onClick={handleSave} disabled={isSaving || isPublished} variant="outline" className="w-full sm:w-auto" style={{ borderColor: secondaryColor || '#1b489b', color: secondaryColor || '#1b489b' }} onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = secondaryColor || '#1b489b';
            e.currentTarget.style.color = 'white';
          }} onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = secondaryColor || '#1b489b';
          }}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Executive Summary */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded flex-shrink-0">
              <BarChart3 className="h-4 w-4 md:h-5 md:w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base md:text-lg font-semibold">The Big Picture</h3>
              <p className="text-xs md:text-sm text-muted-foreground truncate">
                Last Updated: {formatLastUpdated(executiveSummaryUpdatedAt)}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => handleGenerateClick("executive")}
            disabled={generating !== null || isPublished}
            className="w-full sm:w-auto flex-shrink-0"
            style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
          >
            <Sparkles className={`h-4 w-4 md:mr-2 ${generating === "executive" ? "animate-spin" : ""}`} />
            <span className="hidden md:inline">{generating === "executive" ? "Generating..." : "Generate Summary"}</span>
            <span className="md:hidden">{generating === "executive" ? "Generating..." : "Generate"}</span>
          </Button>
        </div>
        <RichTextEditor
          value={execSummary}
          onChange={setExecSummary}
          onSave={(summary) => handleSaveSummary("executive", summary)}
          placeholder="Write a summary for this listing..."
          disabled={isPublished}
        />
      </div>

      {/* Showings & Open Houses Summary */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded flex-shrink-0">
              <Building2 className="h-4 w-4 md:h-5 md:w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base md:text-lg font-semibold">Who Came To See Your Home</h3>
              <p className="text-xs md:text-sm text-muted-foreground truncate">
                Last Refreshed: {formatLastUpdated(showingsSummaryUpdatedAt)}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => handleGenerateClick("showings")}
            disabled={generating !== null || isPublished}
            className="w-full sm:w-auto flex-shrink-0"
            style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
          >
            <Sparkles className={`h-4 w-4 md:mr-2 ${generating === "showings" ? "animate-spin" : ""}`} />
            <span className="hidden md:inline">{generating === "showings" ? "Generating..." : "Generate Summary"}</span>
            <span className="md:hidden">{generating === "showings" ? "Generating..." : "Generate"}</span>
          </Button>
        </div>
        <RichTextEditor
          value={showSummary}
          onChange={setShowSummary}
          onSave={(summary) => handleSaveSummary("showings", summary)}
          placeholder="Write a summary for this listing..."
          disabled={isPublished}
        />
      </div>

      {/* Comps Summary */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded flex-shrink-0">
              <Home className="h-4 w-4 md:h-5 md:w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base md:text-lg font-semibold">How You Compare To Sold Homes</h3>
              <p className="text-xs md:text-sm text-muted-foreground truncate">
                Last Refreshed: {formatLastUpdated(compsSummaryUpdatedAt)}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => handleGenerateClick("comps")}
            disabled={generating !== null || isPublished}
            className="w-full sm:w-auto flex-shrink-0"
            style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
          >
            <Sparkles className={`h-4 w-4 md:mr-2 ${generating === "comps" ? "animate-spin" : ""}`} />
            <span className="hidden md:inline">{generating === "comps" ? "Generating..." : "Generate Summary"}</span>
            <span className="md:hidden">{generating === "comps" ? "Generating..." : "Generate"}</span>
          </Button>
        </div>
        <RichTextEditor
          value={compSummary}
          onChange={setCompSummary}
          onSave={(summary) => handleSaveSummary("comps", summary)}
          placeholder="Write a summary for this listing..."
          disabled={isPublished}
        />
      </div>

      {/* Neighborhood Summary */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded flex-shrink-0">
              <Building2 className="h-4 w-4 md:h-5 md:w-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base md:text-lg font-semibold">The Market Pulse</h3>
              <p className="text-xs md:text-sm text-muted-foreground truncate">
                Last Refreshed: {formatLastUpdated(neighborhoodSummaryUpdatedAt)}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => handleGenerateClick("neighborhood")}
            disabled={generating !== null || isPublished}
            className="w-full sm:w-auto flex-shrink-0"
            style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
          >
            <Sparkles className={`h-4 w-4 md:mr-2 ${generating === "neighborhood" ? "animate-spin" : ""}`} />
            <span className="hidden md:inline">{generating === "neighborhood" ? "Generating..." : "Generate Summary"}</span>
            <span className="md:hidden">{generating === "neighborhood" ? "Generating..." : "Generate"}</span>
          </Button>
        </div>
        <RichTextEditor
          value={neighborhoodSum}
          onChange={setNeighborhoodSum}
          onSave={(summary) => handleSaveSummary("neighborhood", summary)}
          placeholder="Generate a neighborhood market summary..."
          disabled={isPublished}
        />
      </div>

      {/* Agent Insights Summary */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-muted rounded">
              <Lightbulb className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Agent Insights & Recommendations</h3>
              <p className="text-sm text-muted-foreground">
                Last Updated: {agentInsights?.updated_at ? formatLastUpdated(agentInsights.updated_at) : 'Never'}
              </p>
            </div>
          </div>
        </div>
        <RichTextEditor
          value={agentInsightsSummary}
          onChange={setAgentInsightsSummary}
          placeholder="Go to Agent Insights menu to generate strategic insights..."
          disabled={true}
        />
        <p className="text-xs text-muted-foreground">
          Note: This summary is generated from the Agent Insights questionnaire. Go to the Agent Insights menu to answer questions and generate insights.
        </p>
      </div>

      <div className="text-sm text-muted-foreground text-right">
        Pro Tip: Use <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">⌘B</kbd> to bold text, <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">⌘I</kbd> for italic while editing.
      </div>

      <Dialog open={changesDialogOpen} onOpenChange={setChangesDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <DialogTitle className="text-xl">Update with AI</DialogTitle>
            </div>
            <DialogDescription>
              A summary already exists. Describe what changes you'd like the AI to make.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={requestedChanges}
            onChange={(e) => setRequestedChanges(e.target.value)}
            placeholder="E.g., Make it more concise, emphasize the price reduction, add more detail about showings..."
            className="min-h-32"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangesDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmChanges}
              disabled={!requestedChanges.trim()}
              style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Generate Updated Summary
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
